import { Box, Group, Button, ActionIcon, useMantineColorScheme } from "@mantine/core";
import { Play } from "lucide-react";
import { VariableInput } from "@/components/reports";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
    setVariableValues,
    selectChart,
    selectReport,
    selectChartExecuting,
    selectVariableValues
} from "@/store/slices/chartSlice";
import { PREDEFINED_VARIABLES } from "@bi-studio/shared/lib";

interface ChartVariablesBarProps {
    onRun: (values: Record<string, string>) => void;
}

export function ChartVariablesBar({ onRun }: ChartVariablesBarProps) {
    const dispatch = useAppDispatch();
    const chart = useAppSelector(selectChart);
    const report = useAppSelector(selectReport);
    const variableValues = useAppSelector(selectVariableValues);
    const isExecuting = useAppSelector(selectChartExecuting);

    const { colorScheme } = useMantineColorScheme();
    
    if (!chart || !report) return null;

    const userVariables = (report.variables || []).filter(v => !PREDEFINED_VARIABLES.includes(v.name));
    if (userVariables.length === 0) return null;

    return (
        <Box
            p={{ base: 'xs', sm: 'md' }}
            style={{
                backgroundColor: colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.01)' : undefined,
                borderRadius: 'var(--mantine-radius-md)',
                border: '1px solid var(--modal-border)'
            }}
        >
            <Group align="flex-end" gap="xs">
                {userVariables.map(v => (
                    <VariableInput
                        key={v.name}
                        name={v.name}
                        config={v}
                        value={variableValues[v.name] || ""}
                        onChange={(val) => dispatch(setVariableValues({ ...variableValues, [v.name]: val }))}
                        dataSourceId={report.dataSourceId}
                    />
                ))}
                <Button
                    visibleFrom="sm"
                    leftSection={<Play size={16} />}
                    onClick={() => onRun(variableValues)}
                    loading={isExecuting}
                    variant="filled"
                    color={colorScheme === 'dark' ? 'gray.8' : 'brand'}
                >
                    Run Chart
                </Button>
                <ActionIcon
                    hiddenFrom="sm"
                    size="lg"
                    variant="filled"
                    onClick={() => onRun(variableValues)}
                    loading={isExecuting}
                    color={colorScheme === 'dark' ? 'gray.8' : 'brand'}
                >
                    <Play size={20} />
                </ActionIcon>
            </Group>
        </Box>
    );
}
